/**
 * v2Service — client for the additive `v2-*` backend endpoints (Assets +
 * agent generation). Kept SEPARATE from the v1 `apiService` so V1 is untouched.
 *
 * NOTE: these endpoints are not deployed yet — calls will fail at runtime until
 * the `v2-*` lambdas + tables are deployed. The wiring is built "as if deployed".
 */
import axios, { AxiosInstance } from 'axios';
import { API_CONFIG } from './config';
import { attachWorkspaceContext } from './workspaceContext';
import { chatEndpoint, generateEndpoint } from './providerEndpoints';
import { installBanInterceptor } from './banInterceptor';
import { apiService } from './apiService';
import { getIdToken } from '@/auth/firebase';
import { useUpgradeModal, CreditDeclinedError } from '@/v2/upgradeModal';
import { vAlert, vConfirm } from '@/v2/dialogs';
import { useUserStore } from '@/store/userStore';
import i18n from '@/i18n';
import type {
  V2ListVoicesResponse, V2ListStylesResponse,
  V2ListAvatarsResponse, V2ListAvatarLooksResponse,
  V2CreateAvatarResult, V2AvatarStatusResult,
  V2CloneVoiceResult, V2VoiceStatusResult, V2DesignVoiceResult,
  AvatarKind,
  V2Asset,
  V2ListTemplatesResponse, V2Template,
} from './v2Types';

const E = API_CONFIG.endpoints;

// ---------- Templates static catalogue ----------
// The bundled catalogue (public/templates.json) is the source of truth for the
// unified /templates gallery: manual PRESETS (rehosted + branded) plus agent
// visual STYLES (HeyGen, rehosted + branded). The v2-list-templates endpoint is
// tried first but 404s until deployed, so this static file is what actually
// renders. Fetched once, then filtered + paginated in memory to mirror the real
// endpoint's response shape.
let _staticTemplates: V2Template[] | null = null;
async function _loadStaticTemplates(): Promise<V2Template[]> {
  // Only a NON-EMPTY result is memoized: an empty array is truthy, so caching a
  // transient failure (e.g. the file mid-write, or a stale 404) would stick for
  // the whole session and leave the gallery permanently empty. Retry until we
  // actually get rows. `no-cache` forces etag revalidation so a regenerated
  // templates.json is picked up instead of a stale copy from force-cache.
  if (_staticTemplates && _staticTemplates.length) return _staticTemplates;
  try {
    const res = await fetch('/templates.json', { cache: 'no-cache' });
    const json = await res.json();
    const rows = Array.isArray(json?.templates) ? (json.templates as V2Template[]) : [];
    if (rows.length) _staticTemplates = rows;
    return rows;
  } catch {
    return [];
  }
}
async function listTemplatesFromStatic(
  opts: { category?: string; search?: string; limit?: number; cursor?: string | null },
): Promise<V2ListTemplatesResponse> {
  const all = await _loadStaticTemplates();
  const counts = new Map<string, number>();
  for (const t of all) counts.set(t.category, (counts.get(t.category) || 0) + 1);
  const categories = [...counts.entries()]
    .map(([slug, count]) => ({ slug, count }))
    .sort((a, b) => b.count - a.count || a.slug.localeCompare(b.slug));

  const cat = (opts.category || '').toLowerCase();
  const term = (opts.search || '').trim().toLowerCase();
  let filtered = all;
  if (cat && cat !== 'all') filtered = filtered.filter((t) => t.category === cat);
  if (term) filtered = filtered.filter((t) =>
    t.title.toLowerCase().includes(term) ||
    (t.prompt || '').toLowerCase().includes(term) ||
    (t.tags || '').toLowerCase().includes(term));

  const limit = Math.max(1, Math.min(60, opts.limit || 24));
  let offset = 0;
  try { offset = opts.cursor ? Number(JSON.parse(atob(opts.cursor)).o) || 0 : 0; } catch { offset = 0; }
  const page = filtered.slice(offset, offset + limit);
  const nextOffset = offset + page.length;
  const hasMore = nextOffset < filtered.length;
  return {
    templates: page,
    categories,
    total: all.length,
    filtered_total: filtered.length,
    returned: page.length,
    cursor: hasMore ? btoa(JSON.stringify({ o: nextOffset })) : null,
    has_more: hasMore,
  };
}

// ---------- Vivideo agent (LongVid planner) types ----------
export interface V2VideoPlanScene {
  scene_id: number; name: string; start_sec: number; end_sec: number;
  track: 'A Roll' | 'B Roll' | string; voiceover: string; visual: string;
  /** LongVid only: who says what; `voiceover` stays the joined text. */
  speech?: { speaker: string; text: string }[];
}
export interface V2VideoPlan {
  title: string; script: string; style: string;
  avatar?: { name: string }; voice?: { name: string };
  settings: { duration_sec: number; orientation: string; language: string; captions_enabled: boolean; credits_estimate: string };
  scenes: V2VideoPlanScene[];
  /** LongVid only: each attached file and what the video uses it for ("reference" when unstated). */
  files?: { name: string; use: string }[];
  /** The length the customer asked for, when it is past the free cap. */
  requested_duration_sec?: number;
  /** Tier-2 lite project's stand-in plan: never rendered; the real one is written after payment. */
  lite_placeholder?: boolean;
}
export interface V2AgentChange { summary: string; instruction: string }
/** A LongVid storyboard offered for manual approval (video_provider 'longvid_v1'). */
export interface V2AgentStoryboard {
  approval_id: string;
  description: string;
  scenes: { n: number; plot: string; duration_sec: number }[];
  tiers: { budget: number; standard: number; premium: number };
  /** Length the director planned, in seconds — what the card is priced from. */
  planned_sec?: number;
  expires_at: number;
  duration_sec?: number;
  aspect_ratio?: string;
}
export type V2LongvidTier = 'budget' | 'standard' | 'premium';
export interface V2AgentTurn {
  role: 'user' | 'assistant';
  kind: 'text' | 'plan' | 'change' | 'event' | 'video' | 'storyboard';
  text?: string; plan?: V2VideoPlan; change?: V2AgentChange; ts?: string;
  /** Stable id on plan/change turns (for "this plan" scroll + change resolution). */
  id?: string;
  /** kind 'change': locked outcome once the user acts. 'superseded' is a provider
   *  storyboard the director replaced after a revision asked for in chat. */
  resolved?: 'approved' | 'discarded' | 'declined' | 'expired' | 'generated' | 'superseded' | null;
  /** kind 'storyboard' (LongVid): the storyboard card's own payload. */
  storyboard?: V2AgentStoryboard;
  /** Approval turns (plan/storyboard/change): the price in VIVIDEO credits + card id.
   *  A LongVid plan turn carries both — Generate on that card IS the approval. */
  credits?: number; credits_provider?: string;
  items?: { label: string; credits: number }[];
  approval_id?: string; expires_at?: number;
  /** kind 'storyboard': the tier the user approved at. */
  tier?: V2LongvidTier;
  /** kind 'event': which UI action this records ('generate' | 'edit'). */
  event?: string;
  /** kind 'event' (generate): the plan id this render came from (for "this plan"). */
  plan_ref?: string | null;
  /** kind 'video': a finished render kept in the chat (each render persists). */
  video?: { output_url?: string | null; video_url?: string | null; caption_url?: string | null; duration?: number | null };
  /** kind 'text': the free-tier chat-cap steer message. */
  free_limit?: boolean;
  /** kind 'text': a v2 locale key the server chose (e.g. a LongVid 402 notice) —
   *  rendered translated, in place of `text`. */
  i18n_key?: string;
  /** kind 'text' (first user message): the composer's reference files, rendered as
   *  little widgets (icon + name) on the message. Supports multiple attachments. */
  attachments?: Array<{ asset_id?: string; name?: string; type?: string; url?: string | null }>;
  /** kind 'plan': the avatar / voice / brand chosen ON this plan card (PER-CARD cast,
   *  forwarded to HeyGen at Generate). Falls back to the project default when unset. */
  avatar_id?: string | null; avatar_name?: string | null; avatar_image?: string | null;
  voice_id?: string | null; voice_name?: string | null; voice_preview_url?: string | null;
  brand_ref?: string | null; brand_name?: string | null; brand_logo?: string | null;
}
export interface V2AgentProject {
  order_id: string; order_type?: string; order_status?: string; order_name?: string; created_date?: string;
  /** 'auto' = one-shot HeyGen render (no planner/chat) → simple loader→result view. */
  generation_mode?: string | null;
  conversation: V2AgentTurn[]; current_plan?: V2VideoPlan | null;
  /** True while the FIRST plan is still being generated server-side. The order already
   *  exists; the UI shows the thinking loader + polls the project until this clears. */
  planning?: boolean;
  // Cast / brand selection (carried from the home chatbox; the planner is told
  // about these and they're forwarded to HeyGen at Generate).
  avatar_id?: string | null; avatar_name?: string | null; avatar_image?: string | null;
  voice_id?: string | null; voice_name?: string | null; voice_preview_url?: string | null;
  brand_ref?: string | null; brand_name?: string | null; brand_logo?: string | null;
  output_url?: string | null; video_url?: string | null; caption_url?: string | null;
  /** LongVid: the delivered film has burned-in captions, so a captions-free copy of
   *  it can be rendered on demand (longvidCleanCopy). Until it is, both urls are the
   *  same captioned file — that is what makes the CC button worth showing. */
  clean_copy_available?: boolean | null;
  video_duration?: number | null; error_message?: string | null; heygen_session_id?: string | null;
  // Render-state fields (projects categories + the generating UI):
  approved_at?: string | null; approved_duration_sec?: number | null;
  cover_image_url?: string | null; credits_consumed?: number | null;
  // Manual (fal) orders may resolve through the same load path:
  fal_model?: string | null; is_preview?: boolean | null; preview_image_url?: string | null;
  /** The exact prompt the user submitted (manual/auto orders). Shown above the video. */
  prompt?: string | null;
  // Provider-routed orders ('longvid_v1', 'videogen_v1'). This one field is the
  // hinge every provider branch on the project page hangs off — see isProviderRouted.
  video_provider?: string | null;
  /** Set when a LongVid / VideoGen create fell back to HeyGen ('longvid_v1' | 'videogen_v1'):
   *  the video is a HeyGen one, and the A/B analytics keep it out of the real HeyGen numbers. */
  fallback_from?: string | null;
  fallback_reason?: string | null;
  /** Tier-2 lite funnel: opened as a preview with no planner call. */
  lite_funnel?: boolean | null;
  /** 'awaiting_storyboard' | 'awaiting_changes' | 'planning' | 'generating' | … */
  render_phase?: string | null;
  longvid_status?: string | null; longvid_pct?: number | null; longvid_stage_text?: string | null;
  /** A v2 locale key for the current production stage (e.g. 'agentProject.stage.producing').
   *  Preferred over longvid_stage_text, which is English-only. */
  longvid_stage_key?: string | null;
  longvid_tier?: V2LongvidTier | null; longvid_version?: number | null;
  longvid_credits_charged?: number | null;
  longvid_approval_id?: string | null; longvid_approval_expires_at?: number | null;
  /** v2 locale keys for the selected options (avatar / voice / brand) the provider
   *  could not carry over. The chat gets them as turns; other views read these. */
  longvid_asset_notes?: string[] | null;
  // VideoGen orders (`video_provider === 'videogen_v1'`) carry the same render
  // state under their own names — read through the helpers in AgentProjectPage
  // so one code path serves every provider-routed order.
  videogen_run_id?: string | null; videogen_project_id?: string | null;
  videogen_export_id?: string | null;
  videogen_status?: string | null; videogen_pct?: number | null;
  videogen_stage_key?: string | null; videogen_stage_text?: string | null;
  videogen_render_credits?: number | null; videogen_charged_at?: string | null;
  videogen_asset_notes?: string[] | null;
  result?: { kind: 'text' | 'plan' | 'change'; text?: string; plan?: V2VideoPlan; change?: V2AgentChange };
}

/** Cast/brand selection the user makes in the home chatbox (and the plan-card
 *  dropdowns), forwarded to the planner + persisted on the order. */
export interface V2AgentSelectionInput {
  avatarId?: string | null; avatarName?: string | null; avatarImage?: string | null;
  voiceId?: string | null; voiceName?: string | null; voicePreviewUrl?: string | null;
  brandRef?: string | null; brandName?: string | null; brandLogo?: string | null;
  // HeyGen style (a Templates-page "template") + reference file asset_ids (composer
  // attachments). Session-level — applied to the render by v2-heygen-generate.
  styleId?: string | null; styleName?: string | null; fileAssetIds?: string[] | null;
  /** txt/md contents read in the browser — planner-only context, stored on the
   *  order like fileAssetIds (session-level). Never uploaded, never sent to HeyGen. */
  docTexts?: { name: string; text: string }[] | null;
  /** When set, a cast pick applies to THIS plan card (per-card cast), not the project default. */
  planRef?: string | null;
}

/** camelCase selection → the lambda's snake_case body. Omits any field that
 *  wasn't provided (undefined) so it never clobbers a stored selection. */
function selectionPayload(s?: V2AgentSelectionInput): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!s) return out;
  if (s.avatarId !== undefined) out.avatar_id = s.avatarId;
  if (s.avatarName !== undefined) out.avatar_name = s.avatarName;
  if (s.avatarImage !== undefined) out.avatar_image = s.avatarImage;
  if (s.voiceId !== undefined) out.voice_id = s.voiceId;
  if (s.voiceName !== undefined) out.voice_name = s.voiceName;
  if (s.voicePreviewUrl !== undefined) out.voice_preview_url = s.voicePreviewUrl;
  if (s.brandRef !== undefined) out.brand_ref = s.brandRef;
  if (s.brandName !== undefined) out.brand_name = s.brandName;
  if (s.brandLogo !== undefined) out.brand_logo = s.brandLogo;
  if (s.styleId !== undefined) out.style_id = s.styleId;
  if (s.styleName !== undefined) out.style_name = s.styleName;
  if (s.fileAssetIds !== undefined) out.file_asset_ids = s.fileAssetIds;
  if (s.docTexts !== undefined) out.doc_texts = s.docTexts;
  if (s.planRef !== undefined && s.planRef !== null) out.plan_ref = s.planRef;
  return out;
}

// Mirror of the lambda's grouping: names are "<Character> <look description>",
// so the first token identifies the character ("Abigail Office Front" → Abigail).
function characterKey(name?: string): string {
  const n = (name || '').trim();
  return n.split(' ')[0].split('(')[0].trim() || n;
}

function lookLabel(char: string, name?: string): string {
  const n = (name || '').trim();
  let label = n.startsWith(char) ? n.slice(char.length).trim() : n;
  label = label.replace(/^[-–—_\s]+/, '');
  if (label.toLowerCase().startsWith('in ')) label = label.slice(3);
  label = label.trim();
  if (label.startsWith('(') && label.endsWith(')')) label = label.slice(1, -1);
  return label || 'Default';
}

function groupIntoCharacters(avatars: import('./v2Types').V2StockAvatar[]): import('./v2Types').V2AvatarCharacter[] {
  const map = new Map<string, import('./v2Types').V2AvatarCharacter>();
  for (const a of avatars) {
    const key = characterKey(a.name);
    let c = map.get(key);
    if (!c) {
      c = { name: key, gender: a.gender, preview_image_url: a.preview_image_url, preview_video_url: a.preview_video_url, looks_count: 0, looks: [] };
      map.set(key, c);
    }
    c.looks.push({ ...a, label: lookLabel(key, a.name) });
    c.looks_count = c.looks.length;
  }
  return [...map.values()];
}

class V2Service {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseUrl,
      headers: { 'Content-Type': 'application/json' },
      timeout: 70000, // creation/clone calls can take a while
    });
    // Same additive Bearer-token attach as apiService (see the note there): lets
    // the v2-* backend verify identity server-side. Staged — token-less calls are
    // still accepted until each endpoint is flipped to strict, so V2 never breaks.
    this.client.interceptors.request.use(async (config) => {
      config.headers = config.headers ?? {};
      // Tag every call as the web client. The backend only applies the new
      // "2 free then 10 credits" premium-asset metering to web-tagged requests,
      // leaving the not-yet-updated mobile app's paid users unmetered.
      (config.headers as Record<string, string>)['X-Client'] = 'web';
      const token = await getIdToken();
      if (token) {
        (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
      }
      return config;
    });
    // Team workspaces: inject workspace_org_id on workspace-scoped endpoints
    // while a team workspace is active (personal workspace = no-op).
    this.client.interceptors.request.use(attachWorkspaceContext);
    // Block/ban: surface 403 user_banned globally + tear down the session.
    installBanInterceptor(this.client);
    this.client.interceptors.response.use(
      (r) => r,
      async (e) => {
        const data = e.response?.data;
        const status = e.response?.status;
        // Free-tier creation limit (premium assets are paid-only, enforced
        // server-side) → pop the paywall IN PLACE (no jarring full-page
        // redirect). Covers every v2 create flow in one place; create modals
        // close themselves on this error (isFreeLimitError) so only the popup
        // remains.
        // Server-side content moderation refused the request (prompt blocklist,
        // CSAM screen, or Rekognition on an input image). The composer runs the
        // same blocklist as a UX gate, but this fires for direct API calls and
        // for older installed builds that predate the client gate — so it must
        // say something localized rather than surfacing a raw 403.
        if (status === 403 && data?.error === 'content_policy') {
          void vAlert(i18n.t('moderation.refused', { ns: 'v2' }), {
            title: i18n.t('moderation.refusedTitle', { ns: 'v2' }),
            danger: true,
          });
        }
        if (status === 403 && data?.error === 'free_tier_limit') {
          const asset = (data.asset === 'avatar' || data.asset === 'voice' || data.asset === 'brand') ? data.asset : 'avatar';
          useUpgradeModal.getState().show({ kind: 'free_asset_limit', asset });
        }
        // Paid metering: after the 2 free lifetime premium-asset creations, each
        // one costs credits. The backend returns 402 credits_required so we can
        // confirm with the user IN PLACE, then transparently retry the exact same
        // request with confirm_credits:true. Retried once only (guarded).
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const cfg: any = e.config || {};
        if (status === 402 && data?.error === 'credits_required' && !cfg.__creditRetry) {
          const cost = Number(data.credits_required) || 10;
          const ok = await vConfirm(
            i18n.t('paywall.creditCharge.message', { ns: 'v2', credits: cost }),
            {
              title: i18n.t('paywall.creditCharge.title', { ns: 'v2', credits: cost }),
              confirmLabel: i18n.t('paywall.creditCharge.confirm', { ns: 'v2', credits: cost }),
              cancelLabel: i18n.t('paywall.creditCharge.cancel', { ns: 'v2' }),
            },
          );
          if (ok) {
            let reqBody: Record<string, unknown> = {};
            try {
              reqBody = typeof cfg.data === 'string' ? JSON.parse(cfg.data) : (cfg.data || {});
            } catch { reqBody = {}; }
            const resp = await this.client.request({
              ...cfg,
              __creditRetry: true,
              data: JSON.stringify({ ...reqBody, confirm_credits: true }),
            });
            // Credits changed server-side → refresh the header balance.
            void useUserStore.getState().refreshUser();
            return resp;
          }
          // User cancelled the charge → swallow silently (no error toast).
          return Promise.reject(new CreditDeclinedError());
        }
        // Not enough credits to cover the charge → route to the upgrade/credits
        // page via the same in-place paywall popup.
        if (status === 402 && data?.error === 'insufficient_credits') {
          useUpgradeModal.getState().show({ kind: 'insufficient_credits' });
        }
        if (import.meta.env.DEV) {
          console.error('❌ v2 API Error:', status, e.config?.url, e.response?.data);
        }
        return Promise.reject(e);
      }
    );
  }

  // ---------- Reads ----------

  async listVoices(opts: {
    userId?: string; language?: string; gender?: string; search?: string; limit?: number;
  } = {}): Promise<V2ListVoicesResponse> {
    const { data } = await this.client.post<V2ListVoicesResponse>(E.v2ListVoices, {
      user_id: opts.userId, language: opts.language, gender: opts.gender,
      search: opts.search, limit: opts.limit,
    });
    return data;
  }

  async listStyles(opts: { tag?: string; search?: string; limit?: number } = {}): Promise<V2ListStylesResponse> {
    const { data } = await this.client.post<V2ListStylesResponse>(E.v2ListStyles, opts);
    return data;
  }

  /**
   * Templates v2 — the curated video-PRESET gallery. Calls the backend
   * `v2-list-templates` endpoint (DynamoDB-backed, paginated). Until that
   * lambda is deployed it FALLS BACK to the bundled static catalogue
   * (`public/templates.json`) and paginates/filters it in memory, so the
   * gallery renders locally exactly as it will in production.
   */
  async listTemplates(
    opts: { category?: string; search?: string; limit?: number; cursor?: string | null } = {},
  ): Promise<V2ListTemplatesResponse> {
    try {
      const { data } = await this.client.post<V2ListTemplatesResponse>(E.v2ListTemplates, {
        category: opts.category, search: opts.search,
        limit: opts.limit, cursor: opts.cursor ?? undefined,
      });
      if (data && Array.isArray(data.templates)) return data;
      throw new Error('empty');
    } catch {
      return listTemplatesFromStatic(opts);
    }
  }

  /** Avatar catalogue: the user's OWN avatars + public stock bundled into
   *  CHARACTERS (one card per person, looks nested — "Abigail" with 5 looks
   *  instead of 5 separate rows).
   *  Compatibility, newest first:
   *   - redeployed lambda: returns `characters` directly (view=characters)
   *   - current lambda: returns flat `avatars` → grouped client-side here
   *   - oldest lambda: returns `avatar_groups` → normalized to legacy_groups */
  async listAvatars(opts: {
    userId?: string; offset?: number; gender?: string; search?: string; limit?: number; token?: string | null;
  } = {}): Promise<V2ListAvatarsResponse> {
    const { data } = await this.client.post<V2ListAvatarsResponse & { avatar_groups?: unknown; next_token?: string | null }>(
      E.v2ListAvatars,
      {
        user_id: opts.userId, offset: opts.offset, gender: opts.gender, search: opts.search,
        limit: opts.limit, token: opts.token ?? undefined, view: 'characters',
      },
    );
    if (!data.avatars && !data.characters && Array.isArray(data.avatar_groups)) {
      return {
        mode: 'catalogue',
        owned: data.owned || [],
        owned_count: data.owned_count || 0,
        avatars: [],
        returned: data.returned || 0,
        total_stock: 0,
        next_offset: null,
        has_more: !!data.has_more,
        legacy_groups: data.avatar_groups as V2ListAvatarsResponse['legacy_groups'],
        legacy_next_token: data.next_token ?? null,
      };
    }
    if (!data.characters && Array.isArray(data.avatars)) {
      // Deployed lambda predates the characters view → group its flat page
      // client-side (the list is alphabetical, so same-character looks are
      // adjacent; the UI merges across pages by character name).
      data.characters = groupIntoCharacters(data.avatars);
      data.mode = 'characters';
    }
    return data;
  }

  /** Optional: a specific group's looks (fails soft — HeyGen's looks endpoint
   *  is unreliable, so this can return an empty list with looks_error). */
  async listAvatarLooks(groupId: string): Promise<V2ListAvatarLooksResponse> {
    const { data } = await this.client.post<V2ListAvatarLooksResponse>(E.v2ListAvatars, { group_id: groupId });
    return data;
  }

  // ---------- Avatar writes ----------

  async createAvatar(opts: {
    userId: string; kind: AvatarKind; name: string;
    prompt?: string; fileUrl?: string; assetId?: string; consent?: boolean;
    /** Up to 3 image URLs for style/setting guidance (prompt avatars). */
    referenceImages?: string[];
  }): Promise<V2CreateAvatarResult> {
    const { data } = await this.client.post<V2CreateAvatarResult>(E.v2CreateAvatar, {
      user_id: opts.userId, kind: opts.kind, name: opts.name,
      prompt: opts.prompt, file_url: opts.fileUrl, asset_id: opts.assetId, consent: opts.consent,
      reference_images: opts.referenceImages?.slice(0, 3),
    });
    return data;
  }

  async avatarStatus(userId: string, avatarRef: string): Promise<V2AvatarStatusResult> {
    const { data } = await this.client.post<V2AvatarStatusResult>(E.v2AvatarStatus, {
      user_id: userId, avatar_ref: avatarRef,
    });
    return data;
  }

  async createAvatarConsent(userId: string, avatarRef: string, rerouteUrl?: string): Promise<{ avatar_ref: string; consent_url?: string; consent_status?: string | null }> {
    const { data } = await this.client.post(E.v2CreateAvatarConsent, {
      user_id: userId, avatar_ref: avatarRef, reroute_url: rerouteUrl,
    });
    return data;
  }

  /** Rename the user's OWN avatar (HeyGen PATCH on the look + row update). */
  async renameAvatar(userId: string, avatarRef: string, name: string): Promise<{ avatar_ref: string; display_name: string }> {
    const { data } = await this.client.post(E.v2ManageAvatar, {
      user_id: userId, avatar_ref: avatarRef, action: 'rename', name,
    });
    return data;
  }

  /** Delete the user's OWN avatar (HeyGen group delete; our row is kept as audit). */
  async deleteAvatar(userId: string, avatarRef: string): Promise<{ avatar_ref: string; status: string }> {
    const { data } = await this.client.post(E.v2ManageAvatar, {
      user_id: userId, avatar_ref: avatarRef, action: 'delete',
    });
    return data;
  }

  // ---------- Voice writes ----------

  async cloneVoice(opts: {
    userId: string; voiceName: string; s3Url?: string; assetId?: string;
    language?: string; consent: boolean; durationSec?: number;
  }): Promise<V2CloneVoiceResult> {
    const { data } = await this.client.post<V2CloneVoiceResult>(E.v2CloneVoice, {
      user_id: opts.userId, voice_name: opts.voiceName, s3_url: opts.s3Url, asset_id: opts.assetId,
      language: opts.language, consent: opts.consent, duration_sec: opts.durationSec,
    });
    return data;
  }

  async voiceStatus(userId: string, voiceRef: string): Promise<V2VoiceStatusResult> {
    const { data } = await this.client.post<V2VoiceStatusResult>(E.v2VoiceStatus, {
      user_id: userId, voice_ref: voiceRef,
    });
    return data;
  }

  async designVoice(opts: {
    userId: string; prompt: string; gender?: string; locale?: string; seed?: number;
  }): Promise<V2DesignVoiceResult> {
    const { data } = await this.client.post<V2DesignVoiceResult>(E.v2DesignVoice, {
      user_id: opts.userId, prompt: opts.prompt, gender: opts.gender, locale: opts.locale, seed: opts.seed,
    });
    return data;
  }

  /** Persist a chosen designed-voice candidate as the user's own voice. */
  async saveDesignedVoice(opts: {
    userId: string; voiceId: string; name?: string; language?: string; gender?: string; previewAudioUrl?: string;
  }): Promise<{ voice_ref: string; voice_id: string; status: string }> {
    const { data } = await this.client.post(E.v2DesignVoice, {
      user_id: opts.userId, save_voice_id: opts.voiceId, name: opts.name,
      language: opts.language, gender: opts.gender, preview_audio_url: opts.previewAudioUrl,
    });
    return data;
  }

  /** TTS preview — synthesize a short line with a chosen voice (≤300 chars). */
  async speak(opts: {
    userId: string; text: string; voiceId?: string; voiceRef?: string; speed?: number; locale?: string;
  }): Promise<{ audio_url?: string; duration?: number; voice_id?: string }> {
    const { data } = await this.client.post(E.v2VoiceSpeech, {
      user_id: opts.userId, text: opts.text, voice_id: opts.voiceId, voice_ref: opts.voiceRef,
      speed: opts.speed, locale: opts.locale,
    });
    return data;
  }

  /** Refund an agent render stuck in_progress (HeyGen died without firing the
   *  completion webhook). Idempotent + safe: only acts past a long timeout and
   *  only when HeyGen confirms no completed video. Returns reconciled:true if it
   *  marked the order failed + refunded. */
  async reconcileStuckOrder(userId: string, orderId: string): Promise<{ reconciled: boolean; reason?: string; refunded?: number }> {
    const { data } = await this.client.post(E.v2HeygenReconcile, { user_id: userId, order_id: orderId });
    return data;
  }

  /** One-time retention credits for the delete-account funnel. Works for ANY plan
   *  (incl. free) — unlike manage-subscription/yearly_bonus which is sub-gated.
   *  Idempotent server-side: a second call returns granted:false, already_claimed. */
  async grantRetentionCredits(userId: string): Promise<{ granted: boolean; already_claimed?: boolean; credits_added?: number; remaining_credit?: number }> {
    const { data } = await this.client.post(E.v2GrantCredits, { user_id: userId });
    return data;
  }

  // ---------- Asset library (per-user files over the shared HeyGen account) ----------

  async listAssets(userId: string): Promise<{ assets: V2Asset[]; returned: number }> {
    const { data } = await this.client.post(E.v2Assets, { user_id: userId, action: 'list' });
    return data;
  }

  async deleteAsset(userId: string, assetRef: string): Promise<{ asset_ref: string; status: string }> {
    const { data } = await this.client.post(E.v2Assets, { user_id: userId, action: 'delete', asset_ref: assetRef });
    return data;
  }

  /** Full 3-step upload: presigned create → browser PUTs raw bytes straight to
   *  HeyGen's S3 (no Lambda size limits; HeyGen caps files at 32 MB) → complete.
   *  Returns the ready asset row. */
  async uploadAsset(userId: string, file: File, onProgress?: (pct: number) => void): Promise<V2Asset> {
    // Upload via the V1-proven pipeline: the browser uploads to our own R2 bucket
    // (permissive CORS), then the `upload-heygen-asset` lambda forwards the bytes to
    // HeyGen server-side. We deliberately do NOT PUT straight to HeyGen's direct-upload
    // S3 — its bucket CORS whitelists only http://localhost:5173, so browser uploads
    // silently fail on app.vivideo.ai. The returned HeyGen asset_id lives in HeyGen's
    // unified asset library, so it works for BOTH the planner (get-asset resolves it)
    // and v3 video-agents generate (with the {type:'asset_id'} shape we already send).
    // Use the V2 endpoint (v2-upload-heygen-asset), which also accepts PDFs.
    const hg = await apiService.uploadHeygenAsset(userId, file, onProgress, E.v2UploadHeygenAsset);
    const ct = file.type || 'application/octet-stream';
    const assetType = ct.startsWith('image/') ? 'image'
      : ct.startsWith('video/') ? 'video'
      : ct.startsWith('audio/') ? 'audio'
      : ct === 'application/pdf' ? 'document'
      : (hg.fileType || 'file');
    // Record it in the per-user v2 library so it shows in Assets/Files and the planner
    // can resolve its url + type. `url` is the permanent public CDN url (vivideocdn.com),
    // reliable for display and for the planner's vision fetch.
    const { data } = await this.client.post<V2Asset>(E.v2Assets, {
      user_id: userId,
      action: 'register',
      heygen_asset_id: hg.assetId,
      filename: file.name,
      content_type: ct,
      size_bytes: file.size,
      url: hg.publicUrl || hg.url,
      asset_type: assetType,
    });
    onProgress?.(100);
    return data;
  }

  /** Chat message / approval through the GATED v2 endpoint: the lambda
   *  rejects free-tier users server-side (403 free_tier_locked), so no free
   *  message or approval can ever reach HeyGen — regardless of client state. */
  async sendChatMessage(
    userId: string,
    orderId: string,
    message: string,
    options?: {
      autoProceed?: boolean;
      files?: Array<Record<string, unknown>>;
      avatarId?: string;
      voiceId?: string;
      durationSec?: number;
    }
  ): Promise<{ status?: string; autoProceed: boolean; creditsCharged: number }> {
    const { data } = await this.client.post(E.v2HeygenChatMessage, {
      user_id: userId,
      order_id: orderId,
      message,
      auto_proceed: options?.autoProceed,
      files: options?.files,
      avatar_id: options?.avatarId,
      voice_id: options?.voiceId,
      duration_sec: options?.durationSec,
    });
    return { status: data.status, autoProceed: !!data.auto_proceed, creditsCharged: data.credits_charged ?? 0 };
  }

  // ---------- Brand kits ----------

  async listBrands(userId: string): Promise<{ brands: import('./v2Types').V2Brand[]; returned: number }> {
    const { data } = await this.client.post(E.v2Brands, { user_id: userId, action: 'list' });
    return data;
  }

  async createBrand(userId: string, fields: Partial<import('./v2Types').V2Brand> & { name: string }): Promise<import('./v2Types').V2Brand> {
    const { data } = await this.client.post(E.v2Brands, { user_id: userId, action: 'create', ...fields });
    return data;
  }

  async updateBrand(userId: string, brandRef: string, fields: Partial<import('./v2Types').V2Brand>): Promise<import('./v2Types').V2Brand> {
    const { data } = await this.client.post(E.v2Brands, { user_id: userId, action: 'update', brand_ref: brandRef, ...fields });
    return data;
  }

  async deleteBrand(userId: string, brandRef: string): Promise<{ brand_ref: string; status: string }> {
    const { data } = await this.client.post(E.v2Brands, { user_id: userId, action: 'delete', brand_ref: brandRef });
    return data;
  }

  /** Explore a URL and draft brand-kit fields (logo, name, palette, fonts,
   *  motion, identity) for the user to review before creating. Stores nothing. */
  async brandFromUrl(userId: string, url: string): Promise<import('./v2Types').V2BrandFromUrlResult> {
    const { data } = await this.client.post(E.v2BrandFromUrl, { user_id: userId, url });
    return data;
  }

  // ---------- Vivideo agent chat (LongVid planner) ----------

  /** Free-tier gate for AUTO-generate & MANUAL modes. Atomically claims the user's ONE
   *  lifetime free project (the SAME counter the agentic path enforces) the moment they
   *  hit submit — so a free account can create exactly one project across all three modes.
   *  Pro/max are always allowed (unlimited). Returns allowed:false once it's been used. */
  async claimFreeProject(userId: string): Promise<{ allowed: boolean; error?: string; existingOrderId?: string; existingVideoProvider?: string | null }> {
    // A claim belongs to the project about to be created → the user's provider.
    const { data } = await this.client.post(chatEndpoint(), { user_id: userId, action: 'claim_free_project' });
    return {
      allowed: !!data.allowed, error: data.error, existingOrderId: data.existing_order_id,
      // Optional: when the refusal names the project that used the free slot, it
      // can also say who owns it, so "Open my project" lands on the right backend.
      existingVideoProvider: data.video_provider ?? null,
    };
  }

  /** Send a message to Vivideo's own planner. Stateful: stores the conversation +
   *  plan + cast/brand selection on the order. Omit orderId on the FIRST message to
   *  create the project (the returned order_id is the new project). The avatar/voice/
   *  brand selection (from the home chatbox) is forwarded so the planner can tailor
   *  the plan to them. Never touches HeyGen.
   *  asyncReply: paid-tier follow-up turns can outlive API Gateway's ~30s cap, so the
   *  server answers in the background (planning=true) and the planning poll collects
   *  the reply — the request itself returns fast instead of dying at the gateway. */
  async agentChat(opts: V2AgentSelectionInput & {
    userId: string; orderId?: string; message: string; asyncReply?: boolean;
    /** LongVid: the aspect ratio is locked at creation there (length is planned). */
    durationSec?: number; orientation?: string;
    /** The EXISTING project's own provider; omit on the first message so the
     *  user's provider decides which pair creates the project. */
    videoProvider?: string | null;
  }): Promise<V2AgentProject> {
    const { data } = await this.client.post<V2AgentProject>(chatEndpoint(opts.videoProvider), {
      user_id: opts.userId, order_id: opts.orderId, message: opts.message,
      async_reply: opts.asyncReply || undefined,
      duration_sec: opts.durationSec, orientation: opts.orientation,
      ...selectionPayload(opts),
    });
    return data;
  }

  /** Provider approval card: accept the storyboard / changes quote on the card.
   *  The backend calls the provider's /approve and returns the refreshed order.
   *  `videoProvider` is the order's own provider; omitting it keeps the original
   *  LongVid-only home, so existing callers behave exactly as before. */
  async longvidApprove(userId: string, orderId: string, approvalId: string, tier?: V2LongvidTier, videoProvider?: string | null): Promise<V2AgentProject> {
    const { data } = await this.client.post<V2AgentProject>(
      videoProvider === undefined ? E.v2LongvidChat : chatEndpoint(videoProvider), {
        user_id: userId, order_id: orderId, action: 'longvid_approve', approval_id: approvalId, tier,
      });
    return data;
  }

  /** Provider approval card: decline it (storyboard → the order fails;
   *  changes quote → the delivered video stands). */
  async longvidDecline(userId: string, orderId: string, approvalId: string, note?: string, videoProvider?: string | null): Promise<V2AgentProject> {
    const { data } = await this.client.post<V2AgentProject>(
      videoProvider === undefined ? E.v2LongvidChat : chatEndpoint(videoProvider), {
        user_id: userId, order_id: orderId, action: 'longvid_decline', approval_id: approvalId, note,
      });
    return data;
  }

  /** LongVid: ask for the captions-free copy of the DELIVERED film (the CC button).
   *  Free and idempotent — the first call starts the render, and the same call is
   *  polled until it answers 'ready' with the clean url (which becomes the project's
   *  `video_url`). 'busy' means an edit is in flight: try again later. */
  async longvidCleanCopy(userId: string, orderId: string, videoProvider?: string | null): Promise<{
    status: 'none' | 'rendering' | 'ready' | 'failed' | 'busy' | string; video_url?: string | null;
  }> {
    const { data } = await this.client.post(generateEndpoint(videoProvider), {
      user_id: userId, order_id: orderId, action: 'clean_copy',
    });
    return data;
  }

  /** Load / poll a project's state (conversation, plan, video, status) — an empty
   *  message to the same lambda. Also persists a dropdown avatar/voice change.
   *
   *  SELF-CORRECTING: a cold load (deep link, Projects row with no stored
   *  provider) has to guess the pair from the user, and the order may belong to
   *  the other one. Both Lambdas answer a plain load for any of the user's
   *  orders and report the order's real `video_provider`, so a wrong guess is
   *  re-read once from the pair that owns the order — otherwise the page would
   *  keep polling, approving and rendering against the wrong backend. */
  async getAgentProject(userId: string, orderId: string, opts?: V2AgentSelectionInput, videoProvider?: string | null): Promise<V2AgentProject> {
    const path = chatEndpoint(videoProvider);
    const body = { user_id: userId, order_id: orderId, message: '', ...selectionPayload(opts) };
    const { data } = await this.client.post<V2AgentProject>(path, body);
    const owner = chatEndpoint(data?.video_provider ?? null);
    if (owner === path) return data;
    try {
      const corrected = await this.client.post<V2AgentProject>(owner, body);
      return corrected.data;
    } catch (e) {
      // The re-read failed (edge 4xx, a dropped request). The first payload still
      // names the owner, so returning it leaves the page provider-CORRECT and every
      // later call routes to the owning pair — far better than failing the load and
      // leaving the page bound to the guessed one for the rest of the session.
      if (data?.video_provider) return data;
      throw e;
    }
  }

  /** Record the "Generating your video from this plan." chat marker, tagged with the
   *  plan id it came from (so the chat can link "this plan" → that exact card). No
   *  model call. Returns the refreshed project. */
  async recordGenerateEvent(
    userId: string, orderId: string, planRef?: string | null,
    opts?: {
      /** FREE Generate press: also flip the order to a PREVIEW order server-side
       *  (order_status='preview') so the blurred-gate funnel rebuilds from the order
       *  alone on any reload/return — same machinery as the auto/manual free funnels.
       *  The backend guards it (free user, no video, still chat_active). */
      markPreview?: boolean;
      /** The project's own provider (existing order). */
      videoProvider?: string | null;
    },
  ): Promise<V2AgentProject> {
    const { data } = await this.client.post<V2AgentProject>(chatEndpoint(opts?.videoProvider), {
      user_id: userId, order_id: orderId, action: 'record_event', event: 'generate', plan_ref: planRef,
      ...(opts?.markPreview ? { mark_preview: true } : {}),
    });
    return data;
  }

  /** Lock a change card once the user acts (approved / discarded) and tell the planner
   *  the outcome. Persisted on the change turn so it never re-activates. No model call. */
  async resolveChange(userId: string, orderId: string, changeRef: string | undefined, resolution: 'approved' | 'discarded', videoProvider?: string | null): Promise<V2AgentProject> {
    const { data } = await this.client.post<V2AgentProject>(chatEndpoint(videoProvider), {
      user_id: userId, order_id: orderId, action: 'resolve_change', change_ref: changeRef, resolution,
    });
    return data;
  }

  /** Persist a MANUALLY edited plan as the project's current plan (so the planner,
   *  Generate, and future revisions all use the edited version). No model call —
   *  the user's edits are authoritative and must not be "fixed" by the agent. */
  async saveEditedPlan(userId: string, orderId: string, plan: V2VideoPlan, videoProvider?: string | null): Promise<V2AgentProject> {
    const { data } = await this.client.post<V2AgentProject>(chatEndpoint(videoProvider), {
      user_id: userId, order_id: orderId, action: 'save_plan', plan,
    });
    return data;
  }

  /** GATE #1: render a finished plan via HeyGen. The dropdown's HeyGen avatar/voice
   *  ids (or null = auto) + the optional brand kit (brand_ref → logo attach + hidden
   *  guideline injection, server-side). Deducts credits; marks the order in_progress. */
  async generateFromPlan(opts: { userId: string; orderId: string; plan: V2VideoPlan; avatarId?: string | null; voiceId?: string | null; brandRef?: string | null; folderId?: string; videoProvider?: string | null }): Promise<{ order_id: string; session_id: string; status: string; credits_charged: number; duration_sec: number }> {
    const { data } = await this.client.post(generateEndpoint(opts.videoProvider), {
      user_id: opts.userId, order_id: opts.orderId, plan: opts.plan,
      avatar_id: opts.avatarId, voice_id: opts.voiceId, brand_ref: opts.brandRef, folder_id: opts.folderId,
    });
    return data;
  }

  /** AUTO-GENERATE: one-shot HeyGen render straight from the prompt (no planner, no
   *  chat). Same composer/cast/brand as the agent, but treated like a manual video —
   *  HeyGen's mode='generate' auto-proceeds and produces one video; the project page
   *  polls it via pollRenderStatus to completion. Charges OUR credits server-side
   *  (Pro only; free users run the preview funnel client-side). */
  async createAutoOrder(opts: {
    userId: string; prompt: string;
    avatarId?: string | null; voiceId?: string | null; brandRef?: string | null;
    styleId?: string | null; fileAssetIds?: string[] | null;
    orientation?: 'landscape' | 'portrait'; durationSec?: number; folderId?: string;
    /** Free tier: create a persistent PREVIEW order (no charge, no provider call) instead of rendering. */
    preview?: boolean;
  }): Promise<{ orderId: string; sessionId: string; status: string; creditsCharged: number; durationSec: number }> {
    // A new auto order is created with the USER's provider.
    const { data } = await this.client.post(generateEndpoint(), {
      action: 'auto',
      user_id: opts.userId, prompt: opts.prompt,
      avatar_id: opts.avatarId, voice_id: opts.voiceId, brand_ref: opts.brandRef,
      style_id: opts.styleId, file_asset_ids: opts.fileAssetIds,
      orientation: opts.orientation, duration_sec: opts.durationSec, folder_id: opts.folderId,
      preview: opts.preview || undefined,
    });
    return {
      orderId: data.order_id, sessionId: data.session_id, status: data.status,
      creditsCharged: data.credits_charged ?? 0, durationSec: data.duration_sec,
    };
  }

  /** FREE→PAID RESUME: render an existing AUTO preview order in place (charges + HeyGen),
   *  flipping it out of preview — same project, no duplicate. */
  async renderAutoPreview(userId: string, orderId: string, videoProvider?: string | null): Promise<{ orderId: string; status: string; creditsCharged: number }> {
    const { data } = await this.client.post(generateEndpoint(videoProvider), { action: 'auto', user_id: userId, order_id: orderId });
    return { orderId: data.order_id ?? orderId, status: data.status, creditsCharged: data.credits_charged ?? 0 };
  }

  /** PHASE 2 poll for an agent render. Drives HeyGen's two-step flow server-side:
   *  while planning, the lambda detects HeyGen's plan-ready and sends "generate this
   *  video now"; once generating, completion lands via the webhook. Returns the
   *  current render state so the UI can show the loader until output_url appears. */
  async pollRenderStatus(userId: string, orderId: string, videoProvider?: string | null): Promise<{
    order_status?: string; render_phase?: string; heygen_status?: string;
    output_url?: string | null; video_url?: string | null; video_duration?: number | null;
    /** LongVid only — see V2AgentProject.clean_copy_available. */
    clean_copy_available?: boolean | null;
    approved_at?: string | null; approved_duration_sec?: number | null; error_message?: string | null;
    /** HeyGen's own latest chat reply, surfaced verbatim so the user can see
     *  progress — or a refusal — instead of staring at a silent loader. */
    provider_message?: string | null; progress?: number | null;
    /** True when the backend classified HeyGen's reply as a content refusal:
     *  credits are already refunded and the session has been stopped. */
    refusal?: boolean; error_reason?: string | null;
    /** The order's own provider — lets a mis-routed poll correct itself. */
    video_provider?: string | null;
  }> {
    // Same self-correction as getAgentProject: the poll reports the order's own
    // provider, so a guessed pair is re-polled once against the owning one.
    const path = generateEndpoint(videoProvider);
    const body = { user_id: userId, order_id: orderId, action: 'poll' };
    const { data } = await this.client.post(path, body);
    const owner = generateEndpoint(data?.video_provider ?? null);
    if (owner === path) return data;
    try {
      const corrected = await this.client.post(owner, body);
      return corrected.data;
    } catch (e) {
      // Same as the chat load: a failed re-read must still report the owner it
      // learned, so the next tick polls the right pair.
      if (data?.video_provider) return data;
      throw e;
    }
  }

  /** Generate (or fetch the cached) cover image for a plan — a cheap fal image
   *  used as the blurred backdrop on the rendering screen + the plan-card hero.
   *  HeyGen's API exposes no cover URL, so we make our own. Idempotent per order. */
  async generatePlanCover(opts: {
    userId: string; orderId?: string;
    title?: string; concept?: string; style?: string;
    orientation?: 'landscape' | 'portrait' | 'square' | string;
  }): Promise<{ cover_image_url?: string }> {
    const { data } = await this.client.post<{ cover_image_url?: string }>(E.v2PlanCover, {
      user_id: opts.userId,
      order_id: opts.orderId,
      title: opts.title,
      concept: opts.concept,
      style: opts.style,
      orientation: opts.orientation,
    });
    return data;
  }

  /** Live fal queue progress for a manual order — drives the premium loading UI. */
  async getFalStatus(opts: { userId: string; orderId: string }): Promise<{
    status: 'queued' | 'in_progress' | 'rendered' | 'completed' | 'failed' | 'preview';
    queue_position?: number | null;
    percent?: number | null;
    done?: boolean;
  }> {
    const { data } = await this.client.post(E.v2FalStatus, {
      user_id: opts.userId,
      order_id: opts.orderId,
    });
    return data;
  }

  // ---------- Manual mode (new fal models — per-model payload schemas) ----------

  /** Paid-only. The lambda charges OUR credits server-side, submits to the fal
   *  queue and writes a standard Orders row, so the LIVE fal-webhook completes
   *  (or refunds) it like any v1 order. */
  async createManualOrder(opts: {
    userId: string;
    /** Verbatim fal endpoint id, e.g. fal-ai/pixverse/v5/text-to-video */
    falModel: string;
    operation: string;
    orderData: Record<string, unknown>;
    folderId?: string;
    /** Free tier: create a persistent PREVIEW order (no charge, no fal) instead of submitting. */
    preview?: boolean;
  }): Promise<{ orderId: string | null; creditsCharged: number; orderName?: string }> {
    const { data } = await this.client.post<{
      order_id?: string; credits_charged?: number; order_name?: string;
    }>(E.v2CreateOrder, {
      user_id: opts.userId,
      fal_model: opts.falModel,
      operation: opts.operation,
      folder_id: opts.folderId,
      preview: opts.preview || undefined,
      ...opts.orderData,
    });
    return {
      orderId: data.order_id ?? null,
      creditsCharged: data.credits_charged ?? 0,
      orderName: data.order_name,
    };
  }

  /** FREE→PAID RESUME: render an existing MANUAL preview order in place (charges + fal
   *  submit), flipping it out of preview — same project, no duplicate. */
  async renderManualPreview(userId: string, orderId: string): Promise<{ orderId: string | null; creditsCharged: number }> {
    const { data } = await this.client.post<{ order_id?: string; credits_charged?: number }>(
      E.v2CreateOrder, { user_id: userId, order_id: orderId });
    return { orderId: data.order_id ?? orderId, creditsCharged: data.credits_charged ?? 0 };
  }

  // ---------- Public-API key management (Settings → More → API Keys) ----------
  // The plaintext key appears ONLY in the create response — the backend stores a
  // SHA-256 hash. Never persist or log the plaintext client-side.

  async listApiKeys(userId: string): Promise<V2ApiKey[]> {
    const { data } = await this.client.post<{ keys?: V2ApiKey[] }>(
      E.v2ApiKeys, { user_id: userId, action: 'list' });
    return data.keys ?? [];
  }

  /** Returns the new key row INCLUDING the one-time plaintext `api_key`. */
  async createApiKey(userId: string, name: string): Promise<V2ApiKey & { api_key: string }> {
    const { data } = await this.client.post<V2ApiKey & { api_key: string }>(
      E.v2ApiKeys, { user_id: userId, action: 'create', name });
    return data;
  }

  async revokeApiKey(userId: string, keyId: string): Promise<void> {
    await this.client.post(E.v2ApiKeys, { user_id: userId, action: 'revoke', key_id: keyId });
  }

  async renameApiKey(userId: string, keyId: string, name: string): Promise<void> {
    await this.client.post(E.v2ApiKeys, { user_id: userId, action: 'rename', key_id: keyId, name });
  }
}

/** A public-API key as listed by the v2-api-keys lambda (no hash, no plaintext). */
export interface V2ApiKey {
  key_id: string;
  name: string;
  key_prefix: string;
  status: 'active' | 'revoked';
  created_at: number;      // epoch seconds
  last_used_at: number;    // epoch seconds, 0 = never
  total_requests: number;
}

export const v2Service = new V2Service();
export default v2Service;
